import { useEffect, useMemo, useState } from "react"
import { Alert, CircularProgress, Stack } from "@mui/material"
import { Case, CaseElse, Switch } from "lib/@components/switch"
import {
    BREAKDOWN_VIEW_ASSET,
    buildServerAssetRows,
    buildServerScheduleRows,
    hasBillingAdjustment,
} from "./cost-breakdown-view-utils"
import { CostBreakdownTable } from "./cost-breakdown-table"
import { useCostDetailPage } from "./use-cost-detail-page"

const DEFAULT_ROWS_PER_PAGE = 100

// Database-backed asset/schedule view: rows arrive one server page at a time
// (keyset pagination) and search runs server-side; only the per-row fixed-contract
// allocation happens in the browser. Mount keyed by view — see useCostDetailPage.
export function ServerCostBreakdown({ view, costResult, currencySymbol, config, scenarioId, variantId, search }) {
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE)
    const searchTerm = `${search || ""}`.trim()
    const setKey = costResult?.detailsRef?.setKey
    // Also reset on the published generation and the variant: switching variant or a
    // recalculation publishing a new setKey clears the cursor history, so a retained
    // page number would request page 1 while labelling it rows 101-200.
    useEffect(resetPage, [searchTerm, setKey, variantId])

    const { pageData, loading, error } = useCostDetailPage({
        scenarioId,
        variantId,
        setKey: costResult?.detailsRef?.setKey,
        view,
        search: searchTerm,
        page,
        pageSize: rowsPerPage,
    })
    const rows = useMemo(buildRows, [pageData, costResult, view])
    const totals = useMemo(buildTotals, [pageData])
    const showBillingAdjustment = useMemo(checkBillingAdjustments, [rows])

    return (
        <Switch value={getViewState()}>
            <Case when="error">
                <Alert severity="error" data-cy="cost-detail-error">
                    {error?.message || "Could not load this cost detail. Reload the scenario and try again."}
                </Alert>
            </Case>
            <Case when="loading">
                <Stack alignItems="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                </Stack>
            </Case>
            <CaseElse>
                <CostBreakdownTable
                    rows={rows}
                    config={config}
                    totals={totals}
                    costResult={costResult}
                    currencySymbol={currencySymbol}
                    showBillingAdjustment={showBillingAdjustment}
                    isFiltered={!!searchTerm}
                    serverPagination={{
                        count: pageData?.totalCount ?? 0,
                        page,
                        rowsPerPage,
                        hasNextPage: !!pageData?.hasNextPage,
                        loading,
                        onPageChange: handlePageChange,
                        onRowsPerPageChange: handleRowsPerPageChange,
                    }}
                />
            </CaseElse>
        </Switch>
    )

    // A failure must not render as an empty table: that would read as "this scenario
    // has no assets" when the generation is stale or the request was rejected.
    function getViewState() {
        if (error && !pageData) return "error"
        if (loading && !pageData) return "loading"
        return "ready"
    }

    function buildRows() {
        const items = pageData?.items ?? []
        if (view === BREAKDOWN_VIEW_ASSET) return buildServerAssetRows(items, costResult)
        return buildServerScheduleRows(items, costResult)
    }

    // Footer totals span the COMPLETE filtered set (server aggregate), not the page.
    // allocatedCost mirrors the per-row fixed-contract allocation the rows display.
    function buildTotals() {
        const serverTotals = pageData?.totals ?? {}
        return {
            taskCount: serverTotals.taskCount ?? 0,
            actualHours: serverTotals.actualHours ?? 0,
            totalHours: serverTotals.annualHours ?? 0,
            totalCost: serverTotals.allocatedCost ?? serverTotals.annualCost ?? 0,
        }
    }

    function checkBillingAdjustments() {
        return hasBillingAdjustment(rows)
    }

    function resetPage() {
        setPage(0)
    }

    function handlePageChange(nextPage) {
        setPage(nextPage)
    }

    function handleRowsPerPageChange(nextRowsPerPage) {
        setRowsPerPage(nextRowsPerPage)
        setPage(0)
    }
}
